import { RESET_ALL as RESET_ON_LOGOUT, danger, notification } from 'redux-store/reducers/common-reducer';

import { API, destroy, get, put } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';
import { createReducer } from 'utils/redux';

import type { Action, CampaignProductList, ThunkAction } from 'types';

applyTranslations(require('../components/campaign-product-lists/campaign-product-lists.i18n.json'));

const DELETED = 'campaignProductLists/DELETED';
const LOADING = 'campaignProductLists/LOADING';
const LOADED = 'campaignProductLists/LOADED';
const RESET = 'campaignProductLists/RESET';
const SKU_FLEXIBLE = 'campaignProductLists/SKU_FLEXIBLE';

type State = {
  items: Array<CampaignProductList>,
  isLoading: boolean,
  isFlexible: boolean
};

const initialState: State = {
  items: [],
  isLoading: false,
  isFlexible: false
};

export const resetCampaignProductLists = (): Action => ({
  type: RESET
});

const setLoadingStatus = (status: boolean): Action => ({
  type: LOADING,
  payload: { status }
});

const campaignProductListsLoaded = (data: Array<CampaignProductList>): Action => ({
  type: LOADED,

  payload: {
    data
  }
});

const productListDeleted = (id: number): Action => ({
  type: DELETED,

  payload: {
    id
  }
});

const productSKUFlexible = (isFlexible: boolean): Action => ({
  type: SKU_FLEXIBLE,

  payload: {
    isFlexible
  }
});

export function fetchIsFlexibleSKUForCampaign(campaignId: number): ThunkAction {
  const url = API.END_POINTS.CAMPAIGNS.getIsFlexibleSKU(campaignId);

  return async (dispatch) => {
    try {
      const response = await get(url, {}, { suppressGlobalNotification: true });

      dispatch(productSKUFlexible(response.data.isFlexible));
    } catch (error) {
      logError(error);
    }
  };
}

export function toggleFlexibleSKUForCampaign(campaignId: number, isFlexible: boolean): ThunkAction {
  const url = API.END_POINTS.CAMPAIGNS.toggleFlexibleSKU(campaignId);

  return async (dispatch) => {
    try {
      await put(url, {
        data: {
          isFlexible
        }
      }, { suppressGlobalNotification: true });

      dispatch(productSKUFlexible(isFlexible));
      dispatch(notification({
        type: 'success',
        message: __t('campaign_product_lists.toggle_flexible_sku_success_message')
      }));
    } catch (error) {
      logError(error);

      dispatch(notification({
        type: 'danger',
        message: __t('campaign_product_lists.toggle_flexible_sku_error_message')
      }));
    }
  };
}

export function deleteCampaignProductList(campaignId: number, id: number): ThunkAction {
  const url = API.END_POINTS.CAMPAIGNS.deleteProductList(campaignId, id);

  return async (dispatch) => {
    try {
      await destroy(url);
      dispatch(productListDeleted(id));
      return dispatch(notification({
        type: 'success',
        message: __t('campaign_product_lists.delete_list_message_success')
      }));
    } catch (error) {
      dispatch(danger(__t('campaign_product_lists.delete_list_error_message')));
      throw error;
    }
  };
}

export function fetchCampaignProductLists(campaignId: number): ThunkAction {
  const url = API.END_POINTS.CAMPAIGNS.getProductLists(campaignId);

  return async (dispatch) => {
    dispatch(setLoadingStatus(true));

    try {
      const response = await get(url, {}, { suppressGlobalNotification: true });

      dispatch(campaignProductListsLoaded(response.data));
      dispatch(setLoadingStatus(false));
    } catch (error) {
      logError(error);

      dispatch(setLoadingStatus(false));
      throw error;
    }
  };
}

export default createReducer<State, any>('campaignProductLists', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [DELETED]: (state, { id }) => ({
    ...state,
    items: state.items.filter(item => item.id !== id)
  }),
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state, { data }) => ({
    ...state,
    items: data,
    isLoading: false
  }),
  [SKU_FLEXIBLE]: (state, { isFlexible }) => ({
    ...state,
    isFlexible
  })
});
